import { useEffect, useRef, useState } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import otaClient from '@crowdin/ota-client';
import { CROWDIN_DIST, ENV } from '../constants';
import { queryParser } from '../utils';
import en from './en.json';

// constants
const FALLBACK = 'en';
const LS_KEY_TRANSLATIONS = 'i18n.translations';
const client = CROWDIN_DIST ? new otaClient(CROWDIN_DIST) : null;
const inflightRes = new Map();
let localesCache = null;

// utils
function resolveLang(requested, localesList) {
  const { custom_lng } = queryParser(window.location.search);
  if (ENV !== 'production' && custom_lng) return custom_lng;
  const normalize = (lng) => lng.replace('_', '-').toLowerCase(); // e.g. en_US -> en-us
  const baseOf = (lng) => normalize(lng).split('-')[0];
  const req = normalize(requested);
  const available = localesList.map(normalize);

  const exactIdx = available.indexOf(req);
  if (exactIdx >= 0) return localesList[exactIdx];

  // e.g. "en-us" → "en"
  const base = baseOf(req);
  const baseIdx = available.indexOf(base);
  if (baseIdx >= 0) return localesList[baseIdx];

  // e.g. non-exist "en-US" → "en-UK"
  const variantIdx = available.findIndex((k) => baseOf(k) === base);
  if (variantIdx >= 0) return localesList[variantIdx];

  return FALLBACK;
}
function readLS(key) {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}
function writeLS(key, value) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

// requests
async function getLocalesList() {
  if (!client) return {};
  if (localesCache) return localesCache;

  localesCache = client
    .getContent()
    .then((content) => {
      if (!content || typeof content !== 'object' || Array.isArray(content)) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(content).map(([lng, urls]) => [lng, urls[0]])
      );
    })
    .catch((error) => {
      console.error(error);
      return {};
    });

  return localesCache;
}
async function getRes(file) {
  if (!file || !client) return {};

  try {
    const ts = await client.getManifestTimestamp();

    const cached = readLS(LS_KEY_TRANSLATIONS);
    if (cached && cached.ts === ts && cached.files[file]) {
      return cached.files[file];
    }

    const p = inflightRes.get(file);
    if (p) return p;

    const req = client
      .getFileTranslations(file)
      .then((tr) => (tr && typeof tr === 'object' ? tr : {}))
      .catch((e) => {
        console.error(e);
        return {};
      })
      .finally(() => inflightRes.delete(file));

    inflightRes.set(file, req);

    const translations = await req;

    const base = readLS(LS_KEY_TRANSLATIONS);
    const files = base && base.ts === ts ? base.files : {};
    writeLS(LS_KEY_TRANSLATIONS, {
      ts,
      files: { ...files, [file]: translations },
    });

    return translations;
  } catch (e) {
    console.error(e);
    const cached = readLS(LS_KEY_TRANSLATIONS);
    return cached?.files?.[file] ?? {};
  }
}

export function useInitLocales() {
  const [isRtl, setIsRtl] = useState(false);
  const [ready, setReady] = useState(false);
  const [content, setContent] = useState();
  const didRun = useRef(false);

  useEffect(() => {
    if (content) return;

    let aborted = false;
    getLocalesList().then((languages) => {
      if (!aborted) setContent(languages);
    });

    return () => {
      aborted = true;
    };
  }, [content]);

  useEffect(() => {
    if (didRun.current || !content) return;
    didRun.current = true;

    let aborted = false;

    (async () => {
      const raw =
        typeof navigator !== 'undefined' ? navigator.language : FALLBACK;
      const target = resolveLang(raw, Object.keys(content));
      const resByTarget = await getRes(content[target]);
      const hasTargetRes =
        target !== 'en' && Object.keys(resByTarget).length > 0;

      const startResources = {
        en: { translation: en },
      };
      if (hasTargetRes) {
        startResources[target] = { translation: resByTarget };
      }

      if (!i18n.isInitialized) {
        await i18n.use(initReactI18next).init({
          fallbackLng: FALLBACK,
          lng: hasTargetRes ? target : FALLBACK,
          resources: startResources,
          interpolation: { escapeValue: false },
          react: { useSuspense: false },
          debug: ENV !== 'production',
        });
      } else {
        if (hasTargetRes && !i18n.hasResourceBundle(target, 'translation')) {
          i18n.addResourceBundle(
            target,
            'translation',
            resByTarget,
            true,
            false
          );
        }
        if (hasTargetRes && i18n.language !== target) {
          await i18n.changeLanguage(target);
        }
      }

      const dir = i18n.dir(i18n.language ?? FALLBACK);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = i18n.language ?? FALLBACK;
      }

      if (aborted) return;

      setIsRtl(dir === 'rtl');
      setReady(true);
    })().catch((err) => {
      if (ENV !== 'production') console.error('i18n init failed:', err);
      if (!aborted) setReady(true);
    });

    return () => {
      aborted = true;
    };
  }, [content]);

  return { ready, isRtl };
}
